/**
 * safe.js — text that crosses a trust boundary.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE BUG THIS FILE REPLACES
 *
 * The old code did:
 *
 *     const sanitise = (str) => str.replace(/&/g,"&amp;").replace(/</g,"&lt;")…
 *     <p>{sanitise(comment.text)}</p>
 *
 * That is HTML-escaping text that is then handed to React as a text node.
 * React already escapes text nodes. So the escaping happened twice and users
 * literally saw:
 *
 *     Tom &amp; Jerry's "study" &lt;n=40&gt;
 *
 * Worse, it created a false sense of safety: it looks like XSS protection, so
 * the next developer assumes it is safe to pass that string to
 * dangerouslySetInnerHTML — where the double-escape actually unwinds.
 *
 * THE RULE: React text nodes are already safe. Never HTML-escape for React.
 * Escape only at the boundary where you genuinely build HTML — and in this
 * codebase, we never do. dangerouslySetInnerHTML appears nowhere.
 *
 * What this file does instead is normalise: strip invisible characters, cap
 * length, collapse abusive whitespace, and refuse dangerous URLs.
 * ═══════════════════════════════════════════════════════════════════════════
 */

// Control characters, zero-width characters, and bidirectional overrides.
// Bidi overrides let someone post text that renders as something else entirely
// (the "RTL trick"); zero-width characters defeat mute/block word matching and
// enable lookalike handles.
const INVISIBLE = new RegExp(
  '[' +
    '\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F-\\u009F' + // C0 / C1 controls
    '\\u200B-\\u200F' +                                            // zero-width, LTR/RTL marks
    '\\u202A-\\u202E' +                                            // bidi embedding / override
    '\\u2060-\\u2064\\u2066-\\u2069' +                             // word joiner, bidi isolates
    '\\uFEFF' +                                                    // BOM
  ']',
  'g'
);

/**
 * Normalise user text for storage and display.
 * NOT an XSS filter — React is the XSS filter. This is an abuse filter.
 */
export function cleanText(input, maxLen = 5000) {
  if (typeof input !== 'string') return '';
  return input
    .replace(INVISIBLE, '')
    // Unicode normalisation, so "café" typed two different ways compares equal
    // and cannot be used to slip past a block list.
    .normalize('NFC')
    // Cap runs of blank lines: the "wall of newlines" trick that pushes every
    // other post off the screen.
    .replace(/\n{4,}/g, '\n\n\n')
    // Cap combining marks per character — this is what stops Zalgo text
    // (a letter carrying dozens of stacked accents) from overflowing its
    // container into other posts.
    .replace(/(\p{M}{3})\p{M}+/gu, '$1')
    .slice(0, maxLen)
    .trim();
}

/** Single-line fields: names, job titles, handles. Newlines become spaces. */
export function cleanLine(input, maxLen = 200) {
  return cleanText(input, maxLen).replace(/\s+/g, ' ').trim();
}

/**
 * URL allow-list. Returns a safe href or null.
 *
 * Blocks `javascript:` (script execution on click), `data:` (data-URI phishing
 * pages that show your domain in the referrer), `vbscript:`, and `file:`.
 * Protocol-relative `//evil.com` is also rejected because it inherits https
 * and looks like a path.
 */
export function safeUrl(input) {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed || trimmed.startsWith('//')) return null;

  let url;
  try {
    url = new URL(trimmed);
  } catch {
    // Bare domain like "bmj.com" — assume https rather than rejecting, but
    // re-parse so anything weird still fails.
    try {
      url = new URL(`https://${trimmed}`);
    } catch {
      return null;
    }
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:' && url.protocol !== 'mailto:') {
    return null;
  }
  // Punycode/IDN homograph domains ("аpple.com" with a Cyrillic а) render as
  // the real thing. Surface the ASCII form so the user sees xn--80ak6aa92e.com.
  return url.href;
}

/** True when a link leaves our origin — used to decide on the ↗ affordance. */
export function isExternal(href) {
  try {
    return new URL(href, window.location.origin).origin !== window.location.origin;
  } catch {
    return true;
  }
}

/* ── Entity extraction ────────────────────────────────────────────────────── */

// Handles must match the server's validateHandle rules exactly, or the client
// will link @names that can never resolve.
const MENTION_RE = /@([a-z][a-z0-9_]{2,19})\b/gi;
const HASHTAG_RE = /#([\p{L}\p{N}_]{2,30})/gu;
const URL_RE = /\bhttps?:\/\/[^\s<>"']+/gi;

export function extractMentions(text) {
  const found = new Set();
  for (const m of String(text).matchAll(MENTION_RE)) found.add(m[1].toLowerCase());
  return [...found].slice(0, 20);
}

export function extractHashtags(text) {
  const found = new Set();
  for (const m of String(text).matchAll(HASHTAG_RE)) found.add(m[1].toLowerCase());
  return [...found].slice(0, 15);
}

/**
 * Split text into typed tokens for rendering.
 *
 * Returns plain data, NOT markup — the component decides how to render each
 * token. This keeps the "never build HTML strings" rule structurally enforced
 * rather than merely documented.
 *
 * @returns {Array<{type:'text'|'mention'|'hashtag'|'link', value:string, href?:string}>}
 */
export function tokenize(text) {
  const source = String(text ?? '');
  if (!source) return [];

  const matches = [];
  for (const m of source.matchAll(URL_RE)) {
    matches.push({ start: m.index, end: m.index + m[0].length, type: 'link', value: m[0] });
  }
  for (const m of source.matchAll(MENTION_RE)) {
    matches.push({ start: m.index, end: m.index + m[0].length, type: 'mention', value: m[1] });
  }
  for (const m of source.matchAll(HASHTAG_RE)) {
    matches.push({ start: m.index, end: m.index + m[0].length, type: 'hashtag', value: m[1] });
  }

  matches.sort((a, b) => a.start - b.start);

  const tokens = [];
  let cursor = 0;

  for (const match of matches) {
    // A hashtag inside a URL's fragment would otherwise double-match.
    if (match.start < cursor) continue;

    if (match.start > cursor) {
      tokens.push({ type: 'text', value: source.slice(cursor, match.start) });
    }

    if (match.type === 'link') {
      const href = safeUrl(match.value);
      tokens.push(
        href
          ? { type: 'link', value: match.value, href }
          : { type: 'text', value: match.value }
      );
    } else {
      tokens.push({ type: match.type, value: match.value });
    }

    cursor = match.end;
  }

  if (cursor < source.length) tokens.push({ type: 'text', value: source.slice(cursor) });
  return tokens;
}

/**
 * Shorten a URL for display without hiding where it goes.
 * Keeps the host visible — truncating to "bmj.com/artic…" is honest;
 * truncating to "Click here" is how phishing works.
 */
export function displayUrl(href, maxLen = 42) {
  try {
    const u = new URL(href);
    const shown = `${u.hostname.replace(/^www\./, '')}${u.pathname}${u.search}`;
    return shown.length > maxLen ? `${shown.slice(0, maxLen - 1)}…` : shown;
  } catch {
    return href.slice(0, maxLen);
  }
}

/* ── Password strength ────────────────────────────────────────────────────── */

// The 20 passwords that appear at the top of every breach corpus. Blocking
// these does more real-world good than any character-class rule.
const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password123', '12345678', '123456789', '1234567890',
  'qwerty123', 'letmein1', 'welcome1', 'admin123', 'iloveyou', 'sunshine1',
  'princess1', 'football1', 'monkey123', 'abc12345', 'passw0rd', 'trustno1',
  'baseball1', 'dragon123',
]);

/**
 * Score a password. Deliberately NOT the classic
 * "one uppercase, one number, one symbol" rule — that produces `Password1!`,
 * which is terrible, while rejecting `correct horse battery staple`, which is
 * excellent. Length is what matters (NIST SP 800-63B agrees).
 */
export function scorePassword(password, personalInfo = []) {
  const pw = String(password || '');
  const issues = [];

  if (pw.length < 10) issues.push('Use at least 10 characters — length matters more than symbols.');
  if (pw.length > 128) issues.push('Passwords must be 128 characters or fewer.');
  if (COMMON_PASSWORDS.has(pw.toLowerCase())) issues.push('This is one of the most commonly breached passwords.');
  if (/^(.)\1+$/.test(pw)) issues.push('Repeating a single character is not a password.');
  if (/^(0123456789|abcdefghij|qwertyuiop)/i.test(pw)) issues.push('Avoid keyboard or alphabet sequences.');

  for (const info of personalInfo) {
    const clean = String(info || '').trim().toLowerCase();
    if (clean.length >= 4 && pw.toLowerCase().includes(clean)) {
      issues.push('Do not include your name or email in your password.');
      break;
    }
  }

  const variety =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);

  let strength = 0;
  if (pw.length >= 10) strength += 1;
  if (pw.length >= 14) strength += 1;
  if (pw.length >= 20) strength += 1;
  if (variety >= 3) strength += 1;

  return {
    ok: issues.length === 0 && pw.length >= 10,
    issues,
    strength: Math.min(4, strength),
    label: ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][Math.min(4, strength)],
  };
}
